import { Badge, Blockquote } from '@ui';
import Loader from 'components/Loader';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { PaginationMeta } from 'types';
import { Category, CategoryHistoryEntry, CategoryHistorySource } from '~/components/ClubMemberManagement/types';

type Props = {
    clubSlug: string;
    memberId?: number;
};

const getSourceColor = (source: CategoryHistorySource): 'secondary' | 'warning' | 'success' => {
    if (source === 'manual') return 'secondary';
    if (source === 'scheduled') return 'warning';
    return 'success';
};

const ClubMemberCategoryHistoryTimeline = ({ clubSlug, memberId }: Props) => {
    const { t } = useTranslation('member-management');

    const sourceLabels: Record<CategoryHistorySource, string> = {
        manual: t('clubMember.preview.categoryHistory.source.manual'),
        scheduled: t('clubMember.preview.categoryHistory.source.scheduled'),
        dynamic: t('clubMember.preview.categoryHistory.source.dynamic')
    };

    const getActorLabel = (entry: CategoryHistoryEntry) => {
        if (entry.causer?.name) return entry.causer.name;
        if (entry.source === 'dynamic') return t('clubMember.preview.categoryHistory.actor.dynamicRule');
        if (entry.source === 'scheduled') return t('clubMember.preview.categoryHistory.actor.scheduledRun');
        return t('clubMember.preview.categoryHistory.actor.system');
    };

    const { data, isLoading } = useSWR<PaginationMeta<CategoryHistoryEntry>>(
        memberId ? [`club-member/${memberId}/category-history`, { per_page: 12 }] : null
    );
    const { data: categoryData } = useSWR<{ data: Category[] }>(clubSlug ? `club-member/${clubSlug}/category` : null);

    const categoryColorMap = useMemo(
        () =>
            new Map((categoryData?.data ?? []).map((category) => [category.id, category.color ?? '#889096'] as const)),
        [categoryData]
    );

    const entries = data?.data ?? [];

    return (
        <Loader isLoading={isLoading} noBlock>
            <div className="tw-card-flat">
                <div className="tw-mb-3 tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <h5 className="tw-mb-1 tw-font-semibold tw-text-accents-8">
                        {t('clubMember.preview.categoryHistory.title')}
                    </h5>
                    <Badge color="secondary">
                        {t('clubMember.preview.categoryHistory.changesCount', {
                            count: data?.total ?? entries.length
                        })}
                    </Badge>
                </div>

                {entries.length === 0 ? (
                    <Blockquote className="tw-mb-0 tw-text-center">
                        {t('clubMember.preview.categoryHistory.empty')}
                    </Blockquote>
                ) : (
                    <div className="tw-overflow-y-auto">
                        <div className="tw-flex tw-flex-col tw-gap-2.5">
                            {entries.map((entry, index) => (
                                <div
                                    key={entry.id}
                                    className="tw-grid tw-grid-cols-[24px_minmax(0,_1fr)] tw-items-center tw-gap-3"
                                >
                                    <div className="tw-relative tw-flex tw-justify-center">
                                        <span
                                            className={`tw-relative tw-z-[1] tw-h-3 tw-w-3 tw-rounded-full ${
                                                entry.event === 'attached' ? 'tw-bg-success' : 'tw-bg-error'
                                            }`}
                                        />
                                        {index < entries.length - 1 && (
                                            <span className="tw-absolute tw-top-4 tw-h-[calc(100%+16px)] tw-w-px tw-bg-accents-3" />
                                        )}
                                    </div>

                                    <div className="tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white tw-px-3 tw-py-2">
                                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                                            <span
                                                className="tw-inline-flex tw-max-w-full tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-white"
                                                style={{
                                                    backgroundColor:
                                                        categoryColorMap.get(entry.category_id) ?? '#889096'
                                                }}
                                            >
                                                <span className="tw-truncate">{entry.category_name}</span>
                                            </span>
                                            <span className="tw-text-sm tw-font-medium tw-text-accents-6">
                                                {entry.event === 'attached'
                                                    ? t('clubMember.preview.categoryHistory.event.attached')
                                                    : t('clubMember.preview.categoryHistory.event.detached')}
                                            </span>
                                            <span className="tw-ml-auto tw-text-xs tw-leading-5 tw-text-accents-8">
                                                {getActorLabel(entry)} ·{' '}
                                                {moment.parseZone(entry.created_at).format('DD.MM.YYYY, HH:mm')}
                                            </span>
                                            <Badge
                                                size="xs"
                                                color={getSourceColor(entry.source)}
                                                className="tw-w-16 tw-text-center"
                                            >
                                                {sourceLabels[entry.source]}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Loader>
    );
};

export default ClubMemberCategoryHistoryTimeline;
